/**
 * ItemColumnToggle Component — T146P2 US3
 *
 * Per-page column visibility toggles for itemized transaction rows.
 * Desktop (≥640px): Radix Popover
 * Mobile (<640px): shadcn Sheet side="bottom"
 *
 * Data-testids: column-toggle-btn, column-toggle-*, column-toggle-reset-btn
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Eye, RotateCcw } from 'lucide-react';
import { useItemColumnVisibility } from '@/hooks/useDisplaySettings';
import { useWindowSize } from '@/hooks/useWindowSize';

interface ItemColumnToggleProps {
  page: 'transactions' | 'detail' | 'form' | 'review';
  className?: string;
}

export function ItemColumnToggle({ page, className }: ItemColumnToggleProps) {
  const { visibility, toggleColumn, resetColumns } = useItemColumnVisibility(page);
  const [open, setOpen] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width !== undefined && width < 768;

  const columns = [
    { key: 'emoji' as const, label: 'Emoji', data_testid: 'column-toggle-emoji' },
    { key: 'qty' as const, label: 'Quantity', data_testid: 'column-toggle-qty' },
    { key: 'category' as const, label: 'Category', data_testid: 'column-toggle-category' },
    { key: 'notes' as const, label: 'Notes', data_testid: 'column-toggle-notes' },
  ];

  const content = (
    <div className="space-y-3">
      <div className="space-y-2">
        {columns.map((col) => (
          <div key={col.key} className="flex items-center gap-2">
            <Checkbox
              id={`col-${col.key}`}
              checked={visibility[col.key]}
              onCheckedChange={() => toggleColumn(col.key)}
              data-testid={col.data_testid}
            />
            <Label
              htmlFor={`col-${col.key}`}
              className="text-sm font-medium cursor-pointer"
            >
              {col.label}
            </Label>
          </div>
        ))}
      </div>
      <Separator />
      <Button
        variant="outline"
        size="sm"
        onClick={resetColumns}
        data-testid="column-toggle-reset-btn"
        className="w-full text-xs"
      >
        <RotateCcw className="h-3 w-3 mr-1" />
        Reset Columns
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <Button
          variant="outline"
          size="sm"
          asChild
          data-testid="column-toggle-btn"
          className={className ?? ''}
        >
          <SheetTrigger>
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">Columns</span>
          </SheetTrigger>
        </Button>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>Item Columns</SheetTitle>
          </SheetHeader>
          <div className="mt-4">{content}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="column-toggle-btn"
          className={className}
        >
          <Eye className="h-4 w-4" />
          <span className="hidden sm:inline">Columns</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56">
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Item Columns</h4>
          {content}
        </div>
      </PopoverContent>
    </Popover>
  );
}
